import Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import Modify from 'ol/interaction/Modify';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { getLength } from 'ol/sphere';
import Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { MapService, GUIController } from '../map/mapService';
import { useDrawingStore, DrawingMode, DrawingFeature } from '../../stores/drawingStore';
import { useUnifiedElementsStore } from '../../stores/unifiedElementsStore';
import { logger } from '../../../../shared/utils/logger';

// Tipos para el servicio de dibujo
export enum DrawingMode {
  DRAW = 'Dibujar',
  EDIT = 'Editar',
  DELETE = 'Eliminar',
  CONSULT = 'Consulta',
  COPY = 'Copiar',
  SELECT = 'Seleccionar'
}

export interface DrawingConfig {
  defaultCategory?: string;
  enableSnap?: boolean;
  enableRightClickCancel?: boolean;
}

export interface FeatureInfo {
  id: string;
  type: string;
  area: number;
  perimeter: number;
  coordinates: number[][];
  storeyName: string;
  properties: {
    finca?: string;
    computo?: number;
    [key: string]: any;
  };
}

export interface DrawingState {
  mode: DrawingMode;
  isDeleting: boolean;
  isConsulting: boolean;
  isCopying: boolean;
  selectedFeature: any | null;
}

export class DrawingService {
  private mapService: MapService;
  private map: Map | null = null;
  private gui: GUIController | null = null;
  private config: DrawingConfig;
  
  // Interacciones de OpenLayers
  private draw: Draw | null = null;
  private modify: Modify | null = null;
  private snap: Snap | null = null;
  
  // Estado del dibujo
  private state: DrawingState = {
    mode: DrawingMode.DRAW,
    isDeleting: false,
    isConsulting: false,
    isCopying: false,
    selectedFeature: null
  };
  
  // Callbacks
  private modeChangeCallbacks: Array<(mode: DrawingMode) => void> = [];
  private featureCreatedCallbacks: Array<(feature: any) => void> = [];
  private featureDeletedCallbacks: Array<(feature: any) => void> = [];
  private featureSelectedCallbacks: Array<(feature: any) => void> = [];
  


  constructor(mapService: MapService, config: DrawingConfig = {}) {
    this.mapService = mapService;
    this.config = {
      defaultCategory: 'vivienda',
      enableSnap: true,
      enableRightClickCancel: true,
      ...config
    };
  }

  /**
   * Espera a que el mapa esté disponible
   */
  private async waitForMap(): Promise<void> {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (!this.map && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      this.map = this.mapService.getMap();
      attempts++;
    }
    
    if (!this.map) {
      throw new Error('DrawingService: No se pudo obtener el mapa después de múltiples intentos');
    }
    
          logger.log('DRAWING_SERVICE', '✅ DrawingService: Mapa obtenido después de esperar');
  }

  /**
   * Espera a que el mapa esté completamente inicializado y listo
   */
  private async waitForMapReady(): Promise<void> {
    let attempts = 0;
    const maxAttempts = 100; // 10 segundos máximo
    
    while (!this.isReady() && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (!this.isReady()) {
      logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: Mapa no está completamente listo después de esperar');
    } else {
      logger.log('DRAWING_SERVICE', '✅ DrawingService: Mapa completamente inicializado y listo');
    }
  }

  /**
   * Inicializa el servicio de dibujo
   */
  async init(): Promise<void> {
    logger.log('DRAWING_SERVICE', '🔧 DrawingService: Inicializando...');
    
    // Obtener referencias del MapService
    this.map = this.mapService.getMap();
    this.gui = this.mapService.getGUI();
    
    // Verificar que el mapa esté disponible
    if (!this.map) {
      logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: Mapa no disponible, esperando...');
      // Esperar a que el mapa esté disponible
      await this.waitForMap();
    }
    
    if (!this.map || !this.gui) {
      throw new Error('MapService no está inicializado');
    }
    
    // Esperar a que el mapa esté completamente inicializado
    await this.waitForMapReady();
    
    // Configurar listener para cambios de planta
    this.gui.onStoreyChange((storeyId: string) => {
              logger.log('DRAWING_SERVICE', `🔄 DrawingService: Cambio de planta detectado: ${storeyId}`);
      
      // Cargar dibujos del store para la nueva planta
      this.loadDrawingsFromStore(storeyId);
      
      // Si estamos en modo dibujo, reiniciar el modo para usar la nueva capa
      if (this.state.mode === DrawingMode.DRAW) {
        logger.log('DRAWING_SERVICE', '🎨 DrawingService: Reiniciando modo de dibujo para nueva planta');
        this.startDrawMode();
      }
    });
    
    
    
    // Configurar cancelación con clic derecho
    if (this.config.enableRightClickCancel) {
      this.enableRightClickCancel();
    }
    
    logger.log('DRAWING_SERVICE', '✅ DrawingService: Inicializado correctamente');
  }

  /**
   * Habilita la cancelación de dibujo con clic derecho
   */
  private enableRightClickCancel(): void {
    if (!this.map) return;

    this.map.getViewport().addEventListener("contextmenu", (event: any) => {
      event.preventDefault();
      if (this.draw) {
        this.map!.removeInteraction(this.draw);
        this.startDrawMode();
      }
    });
  }

  /**
   * Resetea todas las interacciones
   */
  private resetInteractions(): void {
    if (!this.map) return;

    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = null;
    }
    if (this.modify) {
      this.map.removeInteraction(this.modify);
      this.modify = null;
    }
    if (this.snap) {
      this.map.removeInteraction(this.snap);
      this.snap = null;
    }

    // Remover event listeners
    this.map.un("click", this.showFeatureInfo.bind(this));
    
    // Resetear estado
    this.state.isDeleting = false;
    this.state.isConsulting = false;
    this.state.isCopying = false;
    this.state.selectedFeature = null;
  }

  /**
   * Detiene el modo actual y resetea las interacciones
   */
  public stopCurrentMode(): void {
    logger.log('DRAWING_SERVICE', '🛑 DrawingService: Deteniendo modo actual');
    this.resetInteractions();
    this.updateModeIndicator(DrawingMode.DRAW);
  }

  /**
   * Actualiza el indicador de modo
   */
  private updateModeIndicator(mode: DrawingMode): void {
    this.state.mode = mode;
    
    // Notificar a los callbacks
    this.modeChangeCallbacks.forEach(callback => callback(mode));
    
    logger.log('DRAWING_SERVICE', `🎨 DrawingService: Modo cambiado a ${mode}`);
  }

  /**
   * Inicia el modo de dibujo
   */
  startDrawMode(): void {
    this.resetInteractions();
    this.updateModeIndicator(DrawingMode.DRAW);

    // Verificar que el servicio esté listo
    if (!this.isReady()) {
      logger.error("DRAWING_SERVICE", "❌ DrawingService: Servicio no está listo para iniciar modo de dibujo");
      // Intentar de nuevo después de un breve delay
      setTimeout(() => {
        if (this.isReady()) {
          logger.log("DRAWING_SERVICE", "🔄 DrawingService: Reintentando iniciar modo de dibujo...");
          this.startDrawMode();
        } else {
          logger.error("DRAWING_SERVICE", "❌ DrawingService: Servicio sigue sin estar listo después del reintento");
        }
      }, 500);
      return;
    }

    // Verificar que el mapService esté disponible
    if (!this.mapService) {
      logger.error("DRAWING_SERVICE", "❌ DrawingService: MapService no disponible para iniciar modo de dibujo");
      return;
    }

    // Verificar que el mapa esté disponible
    if (!this.map) {
      logger.error("DRAWING_SERVICE", "❌ DrawingService: Mapa no disponible para iniciar modo de dibujo");
      return;
    }

    // Debug: verificar estado del GUI
    const gui = this.mapService.getGUI();
    if (!gui) {
      logger.error("DRAWING_SERVICE", "❌ DrawingService: GUI no disponible");
      return;
    }
    
    const selectedStoreyId = gui.getSelectedStoreyId();
    logger.log('DRAWING_SERVICE', "🏗️ DrawingService: Planta seleccionada:", selectedStoreyId);
    
    if (!selectedStoreyId || selectedStoreyId === '') {
      logger.warn('DRAWING_SERVICE', "⚠️ DrawingService: No hay planta seleccionada para dibujar");
      logger.log('DRAWING_SERVICE', "🔍 DrawingService: Plantas disponibles:", gui.getStoreyDropdownOptions());
      return;
    }

    const activeLayer = this.mapService.getActiveDrawLayer();
    if (!activeLayer) {
      logger.warn('DRAWING_SERVICE', "⚠️ DrawingService: No se pudo obtener la capa activa de dibujo");
      return;
    }

    // Asegurar que la capa activa esté visible
    activeLayer.setVisible(true);
    logger.log('DRAWING_SERVICE', "✅ DrawingService: Capa activa obtenida y visible para planta:", selectedStoreyId);
    logger.log('DRAWING_SERVICE', "🎯 DrawingService: Usando capa:", activeLayer.get('name') || 'sin nombre');

    const source = activeLayer.getSource();
    if (!source) {
      console.warn("⚠️ DrawingService: No hay source en la capa activa");
      return;
    }
    
    this.draw = new Draw({ source, type: 'Polygon' });
    
    if (this.config.enableSnap) {
      this.snap = new Snap({ source });
    }

    // Configurar evento de finalización de dibujo
    this.draw.on('drawend', (event: any) => {
      const feature = event.feature;
      const type = this.config.defaultCategory || 'vivienda';
      
      // Generar ID único para la feature antes de cualquier operación
      const featureId = `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      feature.setId(featureId);
      feature.set('type', type);
      
      logger.log('DRAWING_SERVICE', '🎨 DrawingService: Creando feature con ID:', featureId, 'categoría:', type);
      
      // Inicializar automáticamente el cómputo con el valor por defecto de la categoría
      // Intentamos obtener el cómputo por categoría desde el store de categorías expuesto por el panel
      let computoDefault = 100;
      try {
        if ((window as any).__categoriesStore) {
          const getCategoryById = (window as any).__categoriesStore.getCategoryById;
          if (getCategoryById) {
            const cat = getCategoryById(type);
            if (cat && cat.computo !== null && !isNaN(Number(cat.computo))) {
              computoDefault = Number(cat.computo);
            }
          }
        }
      } catch (e) {
        logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: No se pudo obtener computo por categoría, usando 100%', e);
      }
      feature.set('computo', computoDefault);
      
      // Inicializar el campo nombre como vacío
      feature.set('nombre', null);
      
      // Aplicar estilo directamente como en app.js
      feature.setStyle(new Style({
        fill: new Fill({ color: this.getColorForCategory(type) }),
        stroke: new Stroke({ color: 'black', width: 2 })
      }));
      
      // Guardar en el store
      this.saveFeatureToStore(feature, type, selectedStoreyId);
      
      // Notificar a los callbacks
      this.featureCreatedCallbacks.forEach(callback => callback(feature));
      
      // Notificar al MapService que los elementos han cambiado
      if (this.mapService && this.mapService.onElementsChanged) {
        this.mapService.onElementsChanged();
      }
      
      logger.log('DRAWING_SERVICE', '✅ DrawingService: Feature creada y guardada en store:', { type, computo: computoDefault });
    });

    // Añadir interacciones al mapa
    this.map!.addInteraction(this.draw);
    if (this.snap) {
      this.map!.addInteraction(this.snap);
    }
    
    logger.log('DRAWING_SERVICE', "✅ DrawingService: Modo de dibujo iniciado correctamente");
  }

  /**
   * Inicia el modo de edición
   */
  startEditMode(): void {
    this.resetInteractions();
    this.updateModeIndicator(DrawingMode.EDIT);

    // Verificar que el mapa esté disponible
    if (!this.map) {
              logger.error("DRAWING_SERVICE", "❌ DrawingService: Mapa no disponible para iniciar modo de edición");
      return;
    }

    const activeLayer = this.mapService.getActiveDrawLayer();
    if (!activeLayer) {
      logger.warn('DRAWING_SERVICE', "⚠️ DrawingService: No hay planta seleccionada para editar");
      return;
    }

    const source = activeLayer.getSource();
    if (!source) {
      console.warn("⚠️ DrawingService: No hay source en la capa activa");
      return;
    }
    
    this.modify = new Modify({ source });
    
    if (this.config.enableSnap) {
      this.snap = new Snap({ source });
    }

    this.map!.addInteraction(this.modify);
    if (this.snap) {
      this.map!.addInteraction(this.snap);
    }
  }

  /**
   * Inicia el modo de eliminación
   */
  startDeleteMode(): void {
    this.resetInteractions();
    this.updateModeIndicator(DrawingMode.DELETE);
    this.state.isDeleting = true;

    // Verificar que el mapa esté disponible
    if (!this.map) {
              logger.error("DRAWING_SERVICE", "❌ DrawingService: Mapa no disponible para iniciar modo de eliminación");
      return;
    }

    this.map.on('click', (event: any) => {
      if (!this.state.isDeleting) return;
      
      const features: any[] = [];
      this.map!.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
        const layers = this.mapService.getLayers();
        if (layers && Object.values(layers.drawLayers).includes(layer)) {
          features.push(feature);
        }
      });
      
      if (features.length > 0) {
        const feature = features[0];
        const storeyId = this.gui?.getSelectedStoreyId();
        
        // Eliminar del store
        if (storeyId && feature.getId()) {
          const drawingStore = useDrawingStore.getState();
          drawingStore.removeDrawing(storeyId, feature.getId());
          logger.log('DRAWING_SERVICE', '🗑️ DrawingService: Feature eliminada del store:', feature.getId());
        }
        
        // Eliminar del mapa
        const layers = this.mapService.getLayers();
        if (layers) {
          const layer = Object.values(layers.drawLayers).find((l: any) => 
            l.getSource()?.hasFeature(feature)
          );
          if (layer && layer.getSource()) {
            layer.getSource().removeFeature(feature);
            
            // Notificar a los callbacks
            this.featureDeletedCallbacks.forEach(callback => callback(feature));
            
            // Notificar al MapService que los elementos han cambiado
            if (this.mapService && this.mapService.onElementsChanged) {
              this.mapService.onElementsChanged();
            }
            
            logger.log('DRAWING_SERVICE', "✅ DrawingService: Polígono eliminado");
          }
        }
      }
    });
  }

  /**
   * Inicia el modo de consulta
   */
  startConsultMode(): void {
    this.resetInteractions();
    this.updateModeIndicator(DrawingMode.CONSULT);
    this.state.isConsulting = true;

    if (!this.map) return;

    this.map.on("click", this.showFeatureInfo.bind(this));
  }

  /**
   * Muestra información de una feature
   */
  private showFeatureInfo(event: any): void {
    if (!this.map || !this.gui) return;

    const selectedFeatures: any[] = [];
    this.map.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
      const layers = this.mapService.getLayers();
      if (layers && Object.values(layers.drawLayers).includes(layer)) {
        selectedFeatures.push(feature);
      }
    });
    
    if (selectedFeatures.length > 0) {
      const feature = selectedFeatures[0];
      const featureInfo = this.getFeatureInfo(feature);
      
      // Notificar a los callbacks
      this.featureSelectedCallbacks.forEach(callback => callback(feature));
      
      logger.log('DRAWING_SERVICE', '📊 DrawingService: Información de feature:', featureInfo);
      
      // Mostrar modal con la información del polígono
      this.showFeatureInfoModal(featureInfo);
    }
  }

  /**
   * Muestra el modal con información detallada de una feature
   */
  private showFeatureInfoModal(featureInfo: FeatureInfo): void {
    // Crear tabla de coordenadas
    const coordsTable = `
      <table class="coordinates-table" style="
        width: 100%;
        border-collapse: collapse;
        margin-top: 8px;
        font-size: 12px;
      ">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="padding: 6px; border: 1px solid #dee2e6; text-align: left;">Punto</th>
            <th style="padding: 6px; border: 1px solid #dee2e6; text-align: left;">X (m)</th>
            <th style="padding: 6px; border: 1px solid #dee2e6; text-align: left;">Y (m)</th>
          </tr>
        </thead>
        <tbody>
          ${featureInfo.coordinates.map((coord, index) => `
            <tr>
              <td style="padding: 6px; border: 1px solid #dee2e6;">${index + 1}</td>
              <td style="padding: 6px; border: 1px solid #dee2e6;">${coord[0].toFixed(3)}</td>
              <td style="padding: 6px; border: 1px solid #dee2e6;">${coord[1].toFixed(3)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    // Crear ventana emergente con estilo
    const dialog = document.createElement('div');
    dialog.style.position = 'fixed';
    dialog.style.top = '50%';
    dialog.style.left = '50%';
    dialog.style.transform = 'translate(-50%, -50%)';
    dialog.style.backgroundColor = '#ffffff';
    dialog.style.padding = '16px';
    dialog.style.borderRadius = '4px';
    dialog.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    dialog.style.border = '1px solid #e0e0e0';
    dialog.style.zIndex = '1000';
    dialog.style.minWidth = '400px';
    dialog.style.maxHeight = '80vh';
    dialog.style.overflowY = 'auto';

    dialog.innerHTML = `
      <h3 style="margin: 0 0 12px 0; color: #333333; font-size: 16px;">Información del polígono</h3>
      <div style="margin-bottom: 16px;">
        <div style="margin-bottom: 8px;">
          <span style="font-weight: 500; color: #495057;">Tipo:</span>
          <span style="color: #333333;">${featureInfo.type}</span>
        </div>
        <div style="margin-bottom: 8px;">
          <span style="font-weight: 500; color: #495057;">Planta:</span>
          <span style="color: #333333;">${featureInfo.storeyName}</span>
        </div>
        <div style="margin-bottom: 8px;">
          <span style="font-weight: 500; color: #495057;">Área:</span>
          <span style="color: #333333;">${featureInfo.area.toFixed(2)} m²</span>
        </div>
        <div style="margin-bottom: 8px;">
          <span style="font-weight: 500; color: #495057;">Perímetro:</span>
          <span style="color: #333333;">${featureInfo.perimeter.toFixed(2)} m</span>
        </div>
        ${featureInfo.properties.computo ? `
          <div style="margin-bottom: 8px;">
            <span style="font-weight: 500; color: #495057;">Cómputo:</span>
            <span style="color: #333333;">${featureInfo.properties.computo}</span>
          </div>
        ` : ''}
        <div style="margin-top: 16px;">
          <span style="font-weight: 500; color: #495057;">Coordenadas (EPSG:25830):</span>
          ${coordsTable}
        </div>
      </div>
      <button id="closeDialog" style="
        background-color: #f8f9fa;
        color: #333333;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 6px 12px;
        cursor: pointer;
        font-size: 14px;
        width: 100%;
      ">Cerrar</button>
    `;

    document.body.appendChild(dialog);

    // Evento para cerrar el modal
    document.getElementById('closeDialog')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });

    // Evento para cerrar con Escape
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        document.body.removeChild(dialog);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Evento para cerrar haciendo clic fuera del modal
    const handleOutsideClick = (event: MouseEvent) => {
      if (event.target === dialog) {
        document.body.removeChild(dialog);
        document.removeEventListener('click', handleOutsideClick);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('click', handleOutsideClick);
  }

  /**
   * Obtiene información detallada de una feature
   */
  getFeatureInfo(feature: any): FeatureInfo {
    if (!this.gui) {
      throw new Error('GUI no disponible');
    }

    // Obtener el ID de la planta activa y su nombre
    const activeStoreyId = this.gui.getSelectedStoreyId();
    const storeyOptions = this.gui.getStoreyDropdownOptions();
    const activeStorey = storeyOptions.find(opt => opt.id === activeStoreyId);
    const storeyName = activeStorey ? activeStorey.name : 'No especificada';
    
    // Obtener la geometría y transformarla a EPSG:25830
    const geometry = feature.getGeometry().clone();
    geometry.transform('EPSG:3857', 'EPSG:25830');
    
    // Calcular área y perímetro
    const area = geometry.getArea();
    const perimeter = getLength(geometry);
    
    // Obtener coordenadas en EPSG:25830
    const coordinates = geometry.getCoordinates()[0];

    return {
      id: feature.getId() || 'unknown',
      type: feature.get('type') || 'unknown',
      area,
      perimeter,
      coordinates,
      storeyName,
      properties: {
        finca: feature.get('finca'),
        computo: feature.get('computo'),
        ...feature.getProperties()
      }
    };
  }

  /**
   * Obtiene el color para una categoría
   */
  private getColorForCategory(category: string): string {
    try {
      // Usar la referencia global al store establecida por el DrawingPanel
      if ((window as any).__categoriesStore) {
        const getCategoryColor = (window as any).__categoriesStore.getCategoryColor;
        if (getCategoryColor) {
          const color = getCategoryColor(category);
          logger.log('DRAWING_SERVICE', `🎨 DrawingService: Color para categoría ${category}:`, color);
          return color || 'rgba(128, 128, 128, 0.7)';
        }
      }
    } catch (error) {
              logger.warn('DRAWING_SERVICE', 'No se pudo obtener el color del store de categorías:', error);
    }
    return 'rgba(128, 128, 128, 0.7)';
  }

  /**
   * Alterna la visibilidad de la capa activa
   */
  toggleActiveLayerVisibility(): void {
    const activeLayer = this.mapService.getActiveDrawLayer();
    if (activeLayer) {
      const visible = activeLayer.getVisible();
      const newVisible = !visible;
      activeLayer.setVisible(newVisible);
      
      // Guardar el estado en el store persistente
      const storeyId = this.gui?.getSelectedStoreyId() || null;
      if (storeyId) {
        // Importar el store dinámicamente para evitar dependencias circulares
        import('../../stores/ifcStore').then(({ useIFCStore }) => {
          const store = useIFCStore.getState();
          store.setStoreyVisibility(storeyId, newVisible);
        }).catch(error => {
          logger.error('DRAWING_SERVICE', '❌ DrawingService: Error al guardar estado de visibilidad:', error);
        });
        
        logger.log('DRAWING_SERVICE', `👁️ DrawingService: Visibilidad de capa ${storeyId} cambiada a ${newVisible}`);
              } else {
          logger.log('DRAWING_SERVICE', `👁️ DrawingService: Visibilidad de capa cambiada a ${newVisible} (sin storeyId)`);
        }
    }
  }

  /**
   * Guarda una feature en el store unificado
   */
  private saveFeatureToStore(feature: any, type: string, storeyId: string): void {
    try {
      // Usar el store unificado
      const unifiedStore = useUnifiedElementsStore.getState();
      
      // Obtener el ID de la feature (ya debería estar asignado)
      const featureId = feature.getId();
      if (!featureId) {
        logger.error('DRAWING_SERVICE', '❌ DrawingService: Feature sin ID válido');
        return;
      }
      
      // Verificar si el elemento ya existe para evitar duplicados
      const existingElement = unifiedStore.getElementById(featureId);
      if (existingElement) {
        logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: Elemento ya existe en store unificado, actualizando:', featureId);
        unifiedStore.updateElement(featureId, {
          category: type,
          area: feature.getGeometry().getArea(),
          finca: feature.get('finca') || '',
          computo: feature.get('computo') || 100,
          nombre: feature.get('nombre') || '',
          geometry: feature.getGeometry(),
          feature: feature,
          updatedAt: new Date().toISOString()
        });
        return;
      }
      
      // Crear elemento unificado usando el ID existente
      const element = {
        id: featureId,
        storeyId: storeyId,
        category: type,
        area: feature.getGeometry().getArea(),
        finca: feature.get('finca') || '',
        computo: feature.get('computo') || 100,
        nombre: feature.get('nombre') || '',
        geometry: feature.getGeometry(),
        feature: feature,
        isImported: false
      };
      
      // Añadir al store unificado
      unifiedStore.addElement(element);
      
      logger.log('DRAWING_SERVICE', '💾 DrawingService: Feature guardada en store unificado:', featureId);
      
      // Mantener compatibilidad con el store de dibujo (opcional)
      try {
        const drawingStore = useDrawingStore.getState();
        const drawingFeature: DrawingFeature = {
          id: featureId,
          type: type,
          geometry: feature.getGeometry().getCoordinates(),
          properties: {
            finca: feature.get('finca') || '',
            computo: feature.get('computo') || 100,
            ...feature.getProperties()
          },
          storeyId: storeyId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        drawingStore.addDrawing(storeyId, drawingFeature);
      } catch (error) {
        logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: No se pudo mantener compatibilidad con store de dibujo:', error);
      }
    } catch (error) {
      logger.error('DRAWING_SERVICE', '❌ DrawingService: Error al guardar feature en store unificado:', error);
    }
  }

  /**
   * Carga dibujos desde el store unificado a la capa activa
   */
  loadDrawingsFromStore(storeyId: string): void {
    try {
      // Usar el store unificado
      const unifiedStore = useUnifiedElementsStore.getState();
      const elements = unifiedStore.getElementsByStorey(storeyId);
      
      // Obtener la capa específica para esta planta
      const layer = this.mapService.getOrCreateDrawLayer(storeyId);
      if (!layer) {
        logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: No se pudo obtener la capa para planta:', storeyId);
        return;
      }
      
      const source = layer.getSource();
      if (!source) {
        logger.warn('DRAWING_SERVICE', '⚠️ DrawingService: No hay source en la capa de la planta');
        return;
      }
      
      // Verificar si la capa ya tiene features
      const existingFeatures = source.getFeatures();
      
      // Cargar elementos desde el store unificado, evitando duplicados
      logger.log('DRAWING_SERVICE', `📥 DrawingService: Cargando ${elements.length} elementos para planta ${storeyId} (capa tiene ${existingFeatures.length} features)`);
      
      let loadedCount = 0;
      elements.forEach(element => {
        if (element.feature) {
          try {
            // Verificar si la feature ya existe en el source usando múltiples métodos
            const existingFeatureById = source.getFeatureById(element.id);
            const allFeatures = source.getFeatures();
            const featureExistsById = allFeatures.some(f => f.getId() === element.id);
            const featureExistsByReference = allFeatures.includes(element.feature);
            
            if (!existingFeatureById && !featureExistsById && !featureExistsByReference) {
              // Solo añadir si no existe de ninguna forma
              source.addFeature(element.feature);
              loadedCount++;
              logger.log('DRAWING_SERVICE', `✅ DrawingService: Elemento ${element.id} cargado para planta ${storeyId}`);
            } else {
              logger.log('DRAWING_SERVICE', `🔄 DrawingService: Elemento ${element.id} ya existe en la capa, saltando`);
            }
          } catch (error) {
            logger.warn('DRAWING_SERVICE', `⚠️ DrawingService: Error al verificar/añadir elemento ${element.id}:`, error);
          }
        }
      });
      
      logger.log('DRAWING_SERVICE', `✅ DrawingService: Cargados ${loadedCount}/${elements.length} elementos desde store unificado para planta ${storeyId}`);
      
      // Notificar al MapService que los elementos han cambiado
      if (this.mapService && this.mapService.onElementsChanged) {
        this.mapService.onElementsChanged();
      }
          } catch (error) {
        logger.error('DRAWING_SERVICE', '❌ DrawingService: Error al cargar elementos desde store unificado:', error);
      }
  }



  /**
   * Crea una feature de OpenLayers desde un DrawingFeature del store
   * @deprecated Usar elementos del store unificado en su lugar
   */
  public createFeatureFromDrawing(drawing: DrawingFeature): any {
    try {
      // Crear la feature usando las importaciones directas
      const feature = new Feature({
        geometry: new Polygon(drawing.geometry)
      });
      
      // Restaurar ID
      feature.setId(drawing.id);
      
      // Restaurar propiedades
      feature.set('type', drawing.type);
      feature.set('finca', drawing.properties.finca);
      feature.set('computo', drawing.properties.computo);
      feature.set('nombre', drawing.properties.nombre || null);
      
      // Restaurar estilo
      feature.setStyle(new Style({
        fill: new Fill({ color: this.getColorForCategory(drawing.type) }),
        stroke: new Stroke({ color: 'black', width: 2 })
      }));
      
      return feature;
    } catch (error) {
              logger.error('DRAWING_SERVICE', '❌ DrawingService: Error al crear feature desde drawing:', error);
      return null;
    }
  }

  /**
   * Crea una feature de OpenLayers desde un elemento del store unificado
   */
  public createFeatureFromUnifiedElement(element: any): any {
    try {
      // Si ya tiene feature, devolverla
      if (element.feature) {
        return element.feature;
      }
      
      // Crear nueva feature
      const feature = new Feature({
        geometry: element.geometry
      });
      
      // Restaurar ID
      feature.setId(element.id);
      
      // Restaurar propiedades
      feature.set('type', element.category);
      feature.set('finca', element.finca);
      feature.set('computo', element.computo);
      feature.set('nombre', element.nombre);
      
      // Restaurar estilo
      feature.setStyle(new Style({
        fill: new Fill({ color: this.getColorForCategory(element.category) }),
        stroke: new Stroke({ color: 'black', width: 2 })
      }));
      
      return feature;
    } catch (error) {
              logger.error('DRAWING_SERVICE', '❌ DrawingService: Error al crear feature desde elemento unificado:', error);
      return null;
    }
  }

  /**
   * Obtiene el estado actual del dibujo
   */
  getState(): DrawingState {
    return { ...this.state };
  }

  /**
   * Obtiene el modo actual
   */
  getCurrentMode(): DrawingMode {
    return this.state.mode;
  }

  /**
   * Verifica si el servicio está listo
   */
  isReady(): boolean {
    if (!this.map || !this.mapService) {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: map o mapService no disponibles');
      return false;
    }
    
    const gui = this.mapService.getGUI();
    if (!gui) {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: GUI no disponible');
      return false;
    }
    
    const selectedStoreyId = gui.getSelectedStoreyId();
    if (!selectedStoreyId || selectedStoreyId === '') {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: No hay planta seleccionada');
      return false;
    }
    
    const activeLayer = this.mapService.getActiveDrawLayer();
    if (!activeLayer) {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: No hay capa activa de dibujo');
      return false;
    }
    
    const source = activeLayer.getSource();
    if (!source) {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: No hay source en la capa activa');
      return false;
    }
    
    // Verificar que el mapa esté completamente inicializado
    try {
      const mapView = this.map.getView();
      if (!mapView || !mapView.getCenter()) {
        logger.warn('DRAWING_SERVICE', '⚠️ isReady: Mapa no completamente inicializado');
        return false;
      }
    } catch (error) {
      logger.warn('DRAWING_SERVICE', '⚠️ isReady: Error al verificar vista del mapa:', error);
      return false;
    }
    
    logger.log('DRAWING_SERVICE', '✅ isReady: Servicio listo');
    return true;
  }



  /**
   * Registra callback para cambios de modo
   */
  onModeChange(callback: (mode: DrawingMode) => void): void {
    this.modeChangeCallbacks.push(callback);
  }

  /**
   * Registra callback para features creadas
   */
  onFeatureCreated(callback: (feature: any) => void): void {
    this.featureCreatedCallbacks.push(callback);
  }

  /**
   * Registra callback para features eliminadas
   */
  onFeatureDeleted(callback: (feature: any) => void): void {
    this.featureDeletedCallbacks.push(callback);
  }

  /**
   * Registra callback para features seleccionadas
   */
  onFeatureSelected(callback: (feature: any) => void): void {
    this.featureSelectedCallbacks.push(callback);
  }

  /**
   * Limpia todos los callbacks
   */
  clearCallbacks(): void {
    this.modeChangeCallbacks = [];
    this.featureCreatedCallbacks = [];
    this.featureDeletedCallbacks = [];
    this.featureSelectedCallbacks = [];
  }

  /**
   * Destruye el servicio y limpia recursos
   */
  destroy(): void {
    this.resetInteractions();
    this.clearCallbacks();
    
    this.map = null;
    this.gui = null;
    logger.log('DRAWING_SERVICE', '🗑️ DrawingService: Recursos liberados');
  }
}

// Exportar una instancia singleton (opcional)
let drawingServiceInstance: DrawingService | null = null;

export function createDrawingService(
  mapService: MapService, 
  config?: DrawingConfig
): DrawingService {
  if (drawingServiceInstance) {
    drawingServiceInstance.destroy();
  }
  drawingServiceInstance = new DrawingService(mapService, config);
  return drawingServiceInstance;
}

export function getDrawingService(): DrawingService | null {
  return drawingServiceInstance;
}
